import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useCart } from "../context/CartContext";
import Loading from "../components/Loading";
import { productService, categoryService } from "../services/api";
import ProductCard from "../components/ProductCard";
import { FiShoppingCart, FiTruck } from "react-icons/fi";

const cn = (...cls) => cls.filter(Boolean).join(" ");

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getBaseUnitPrice = (product) => {
  const promo = toNumber(product?.promotional_price);
  const sell = toNumber(product?.selling_price);
  if (promo > 0) return promo;
  if (sell > 0) return sell;
  return toNumber(product?.price);
};

const normalizeColors = (product) => {
  const raw = product?.colors ?? product?.color ?? product?.color_name ?? null;

  let parsed = raw;

  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw;
    }
  }

  if (typeof parsed === "string") {
    const name = parsed.trim();
    return name ? [{ name }] : [];
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((c) => {
        if (!c) return null;
        if (typeof c === "string") return { name: c };
        return {
          id: c.id ?? c.value ?? c.name,
          name: c.name ?? c.label ?? c.value,
          hex: c.hex ?? c.code ?? c.color ?? null,
        };
      })
      .filter((c) => c && c.name);
  }

  return [];
};

const renderDescription = (text) => {
  if (!text) return null;

  const paragraphs = String(text)
    .split(/\n\s*\n/g)
    .map((p) => p.trim())
    .filter(Boolean);

  if (!paragraphs.length) return null;

  return paragraphs.map((p, idx) => (
    <p key={idx} className="mb-4 last:mb-0">
      {p}
    </p>
  ));
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImg, setActiveImg] = useState(0);
  const [selectedPieces, setSelectedPieces] = useState(1);

  const carouselRef = useRef(null);
  const baseUrl = "https://pro-dash.orima.ma/";

  useEffect(() => {
    if (!id) return;
    loadProduct();
    setActiveImg(0);
    setSelectedPieces(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await productService.getProduct(id);
      const productData = response?.data || response;

      if (!productData?.id) throw new Error("Product not found");
      setProduct(productData);

      if (productData.category_id) {
        try {
          const similarsResponse = await categoryService.getCategoryProducts(productData.category_id);
          const similarsData = similarsResponse?.data || similarsResponse;

          const filtered = Array.isArray(similarsData)
            ? similarsData.filter((p) => p.id !== Number(id))
            : [];

          setSimilarProducts(filtered.slice(0, 10));
        } catch {
          setSimilarProducts([]);
        }
      }
    } catch {
      setError("Produit non trouvé");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const stockQuantity = toNumber(product?.stock_quantity) || 0;

  const images = useMemo(() => {
    if (!product) return [];
    const list = [];
    if (product.main_image) list.push(product.main_image);

    if (product.gallery_images) {
      try {
        const parsed =
          typeof product.gallery_images === "string"
            ? JSON.parse(product.gallery_images)
            : product.gallery_images;

        if (Array.isArray(parsed)) list.push(...parsed);
      } catch {
        // ignore
      }
    }
    return list.filter(Boolean);
  }, [product]);

  const activeImageSrc = useMemo(() => {
    const p = images[activeImg];
    if (!p) return "https://via.placeholder.com/1200x900?text=No+Image";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    return `${baseUrl}${p}`;
  }, [images, activeImg]);

  const hasDiscount = useMemo(() => {
    const promo = toNumber(product?.promotional_price);
    const sell = toNumber(product?.selling_price);
    return promo > 0 && sell > 0 && promo < sell;
  }, [product]);

  const baseUnit = useMemo(() => getBaseUnitPrice(product), [product]);

  const discountRate = useMemo(() => {
    if (selectedPieces >= 3) return 0.12;
    if (selectedPieces === 2) return 0.08;
    return 0;
  }, [selectedPieces]);

  const discountLabel = useMemo(() => {
    if (selectedPieces >= 3) return "3 pièces (-12%)";
    if (selectedPieces === 2) return "2 pièces (-8%)";
    return "1 pièce";
  }, [selectedPieces]);

  const discountedUnit = useMemo(() => baseUnit * (1 - discountRate), [baseUnit, discountRate]);

  const totalPrice = useMemo(
    () => (discountedUnit * selectedPieces).toFixed(2),
    [discountedUnit, selectedPieces]
  );

  const colors = useMemo(() => normalizeColors(product), [product]);
  const [selectedColor, setSelectedColor] = useState(null);

  useEffect(() => {
    if (colors.length) setSelectedColor(colors[0]?.name || null);
    else setSelectedColor(null);
  }, [colors]);

  const handleCommander = () => {
    if (!product || stockQuantity <= 0) return;

    addToCart(product, selectedPieces, {
      unitPrice: discountedUnit,
      discountRate,
      discountLabel,
      selectedPieces,
      selectedColor,
    });

    navigate("/checkout");
  };

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;
    const width = carouselRef.current.offsetWidth;
    carouselRef.current.scrollBy({
      left: direction === "left" ? -width : width,
      behavior: "smooth",
    });
  };

  const getMetaDescription = () => {
    if (!product) return "Découvrez nos produits de luxe sur ORIMA";
    const description = product.short_description || product.long_description || "";
    const category = product.category?.name || "accessoire de luxe";
    return `${product.name} - ${category}. ${description.substring(0, 120)}...`;
  };

  if (loading) return <Loading />;

  if (error || !product) {
    return (
      <>
        <Helmet>
          <title>Produit non trouvé | ORIMA</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-white p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">{error || "Produit non trouvé"}</h2>
            <button
              onClick={() => navigate("/shop")}
              className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-all"
            >
              Retour à la boutique
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{product.name} | ORIMA</title>
        <meta name="description" content={getMetaDescription()} />
        <link rel="canonical" href={`https://pro-dash.orima.ma/product/${product.id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} - ORIMA`} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:image" content={activeImageSrc} />
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8 pb-16 sm:pb-10">
          {/* breadcrumb */}
          <nav className="text-xs sm:text-sm text-gray-500 mb-4">
            <span onClick={() => navigate("/")} className="cursor-pointer hover:text-gray-900">
              Accueil
            </span>
            <span className="mx-2">/</span>
            <span onClick={() => navigate("/shop")} className="cursor-pointer hover:text-gray-900">
              Boutique
            </span>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium truncate inline-block max-w-[180px]">
              {product.name}
            </span>
          </nav>

          {/* 12-col grid so image is bigger on desktop */}
          <div className="grid grid-cols-1 gap-7 lg:grid-cols-12">
            {/* LEFT: sticky gallery */}
            <div className="lg:col-span-7 lg:sticky lg:top-6 lg:self-start">
              <div className="flex gap-4">
                {/* thumbs desktop */}
                <div className="hidden sm:flex w-16 flex-col gap-3">
                  {images.map((src, idx) => {
                    const thumb = src.startsWith("http") ? src : `${baseUrl}${src}`;
                    return (
                      <button
                        key={`${src}-${idx}`}
                        onClick={() => setActiveImg(idx)}
                        className={cn(
                          "h-16 w-16 overflow-hidden rounded-lg border bg-white",
                          idx === activeImg ? "border-gray-900" : "border-gray-200"
                        )}
                        aria-label={`thumb-${idx}`}
                      >
                        <img src={thumb} alt="" className="h-full w-full object-cover" />
                      </button>
                    );
                  })}
                </div>

                {/* main image: bigger on desktop */}
                <div className="flex-1">
                  <div
                    className={cn(
                      "relative w-full overflow-hidden rounded-xl bg-gray-50 border border-gray-100",
                      "aspect-square sm:aspect-[4/3] lg:aspect-[4/5]",
                      "max-h-[820px]"
                    )}
                  >
                    <img src={activeImageSrc} alt={product.name} className="h-full w-full object-contain" />
                    {hasDiscount && (
                      <div className="absolute top-3 right-3 rounded-full bg-red-500 px-3 py-1 text-xs font-bold text-white">
                        PROMO
                      </div>
                    )}
                  </div>

                  {/* thumbs mobile */}
                  <div className="mt-3 flex gap-3 overflow-x-auto sm:hidden">
                    {images.map((src, idx) => {
                      const thumb = src.startsWith("http") ? src : `${baseUrl}${src}`;
                      return (
                        <button
                          key={`${src}-m-${idx}`}
                          onClick={() => setActiveImg(idx)}
                          className={cn(
                            "h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg border bg-white",
                            idx === activeImg ? "border-gray-900" : "border-gray-200"
                          )}
                          aria-label={`thumb-m-${idx}`}
                        >
                          <img src={thumb} alt="" className="h-full w-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: product info */}
            <div className="lg:col-span-5">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{product.name}</h1>

              {/* Price */}
              <div className="mt-4">
                <div className="text-3xl font-bold text-gray-900">{totalPrice} MAD</div>

                <div className="mt-1 text-sm text-gray-600">
                  Prix unitaire: {discountedUnit.toFixed(2)} MAD
                  {discountRate > 0 && (
                    <span className="ml-2 font-semibold text-green-700">{discountLabel}</span>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {hasDiscount && (
                    <span className="text-sm font-semibold text-red-600">
                      Prix promo: {toNumber(product.promotional_price).toFixed(2)} MAD
                    </span>
                  )}
                  {toNumber(product.selling_price) > 0 && (
                    <span className={cn("text-sm text-gray-500", hasDiscount ? "line-through" : "")}>
                      Prix normal: {toNumber(product.selling_price).toFixed(2)} MAD
                    </span>
                  )}
                </div>
              </div>

              {/* Pricing tiles */}
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { pieces: 1, label: "1 pièce+" },
                  { pieces: 2, label: "2 pièces+" },
                  { pieces: 3, label: "3 pièces+" },
                ].map((opt) => {
                  const rate = opt.pieces >= 3 ? 0.12 : opt.pieces === 2 ? 0.08 : 0;
                  const unit = (baseUnit * (1 - rate)).toFixed(2);
                  const active = selectedPieces === opt.pieces;

                  return (
                    <button
                      key={opt.pieces}
                      onClick={() => setSelectedPieces(opt.pieces)}
                      className={cn(
                        "rounded-2xl border px-3 py-4 text-center transition-all duration-300",
                        active ? "border-gray-900 shadow-lg pricing-active" : "border-gray-200 hover:border-gray-400"
                      )}
                    >
                      <div className={cn("text-lg font-extrabold", active ? "text-gray-900" : "text-gray-800")}>
                        {unit} MAD
                      </div>
                      <div className="text-sm text-gray-500">{opt.label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Livraison: hidden on mobile */}
              <div className="mt-6 sm:block">
                <div className="flex items-center justify-between">
                  <div className="text-lg sm:text-xl font-semibold text-gray-900">Livraison</div>
                  <div className="text-2xl text-gray-400">{">"}</div>
                </div>

                <div className="mt-3 flex items-center justify-between rounded-xl bg-rose-500 px-4 py-3 text-white">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center">
                      <FiTruck className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold">Livraison gratuite partout au Maroc</div>
                      <div className="text-xs text-white/90 mt-1">1 à 2 jours</div>
                    </div>
                  </div>

                  <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-rose-600">
                    Gratuit
                  </div>
                </div>
              </div>

              {/* Colors */}
              {colors.length > 0 && (
                <div className="mt-6">
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    Couleur: <span className="font-bold">{selectedColor}</span>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {colors.map((c) => {
                      const active = selectedColor === c.name;
                      return (
                        <button
                          key={c.id ?? c.name}
                          onClick={() => setSelectedColor(c.name)}
                          title={c.name}
                          aria-label={c.name}
                          className={cn(
                            "h-11 min-w-11 px-3 rounded-xl border text-sm font-semibold transition-all",
                            active ? "border-gray-900 shadow" : "border-gray-200 hover:border-gray-400"
                          )}
                          style={
                            c.hex
                              ? { backgroundColor: c.hex, color: "#fff", borderColor: active ? "#111827" : "" }
                              : {}
                          }
                        >
                          {c.hex ? "" : c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Desktop Commander only */}
              <div className="mt-6 hidden sm:block">
                <button
                  onClick={handleCommander}
                  disabled={stockQuantity <= 0}
                  className={cn(
                    "w-full rounded-2xl px-6 py-4 text-lg font-extrabold flex items-center justify-center gap-3 transition-transform active:scale-95",
                    stockQuantity <= 0
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-yellow-200 text-gray-900 hover:bg-yellow-300 btn-attire-yellow"
                  )}
                >
                  <FiShoppingCart className="w-6 h-6" />
                  <span>{stockQuantity <= 0 ? "Rupture de stock" : "Commander maintenant"}</span>
                </button>
              </div>

              {/* Description */}
              {(product.long_description || product.short_description) && (
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Description</h3>
                  <div
                    className="mt-3 text-gray-700 leading-8 text-[15px] sm:text-[16px]"
                    style={{ fontFamily: "ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif" }}
                  >
                    {renderDescription(product.long_description || product.short_description)}
                  </div>
                </div>
              )}

              {/* tiny spacer for mobile sticky */}
              <div className="h-4 sm:hidden" />
            </div>
          </div>

          {/* Similar products after description */}
          {similarProducts.length > 0 && (
            <div className="mt-8 sm:mt-14">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Produits similaires</h2>
                <button
                  onClick={() => navigate("/shop")}
                  className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 font-medium"
                >
                  Voir tout →
                </button>
              </div>

              <div className="relative">
                <button
                  onClick={() => scrollCarousel("left")}
                  className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -left-5 z-10 bg-white border-2 border-gray-200 p-3 rounded-full shadow-lg hover:bg-gray-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => scrollCarousel("right")}
                  className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-5 z-10 bg-white border-2 border-gray-200 p-3 rounded-full shadow-lg hover:bg-gray-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div ref={carouselRef} className="flex gap-3 sm:gap-6 overflow-x-auto scroll-smooth hide-scrollbar pb-2">
                  {similarProducts.map((prod) => (
                    <div key={prod.id} className="flex-shrink-0 w-[45%] sm:w-[30%] lg:w-[23%]">
                      <ProductCard product={prod} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MOBILE sticky bottom */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white z-50">
          <div className="mx-auto max-w-7xl px-4 py-3">
            <button
              onClick={handleCommander}
              disabled={stockQuantity <= 0}
              className={cn(
                "w-full rounded-2xl px-6 py-4 text-lg font-extrabold flex items-center justify-center gap-2 transition-transform active:scale-95",
                stockQuantity <= 0 ? "bg-gray-200 text-gray-500" : "bg-yellow-200 text-gray-900 btn-attire-yellow"
              )}
            >
              <FiShoppingCart className="w-6 h-6" />
              <span>{stockQuantity <= 0 ? "Rupture" : "Commander maintenant"}</span>
            </button>

            <div className="mt-2 text-xs text-gray-600 text-center">
              Unité: {discountedUnit.toFixed(2)} MAD • Total: {totalPrice} MAD • {discountLabel}
            </div>
          </div>
        </div>

        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

          @keyframes attractYellow {
            0%, 100% { transform: scale(1); box-shadow: 0 10px 35px rgba(0,0,0,0.12); }
            50% { transform: scale(1.03); box-shadow: 0 18px 55px rgba(0,0,0,0.22); }
          }
          .btn-attire-yellow { animation: attractYellow 1.6s ease-in-out infinite; }

          @keyframes tileGlow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          .pricing-active { animation: tileGlow 1.4s ease-in-out infinite; }
        `}</style>
      </div>
    </>
  );
};

export default ProductDetail;
